import { Fragment, useEffect, useRef, useState } from 'react'
import { Menu, Transition } from '@headlessui/react'
import moment from 'moment'
import Flatpickr from 'react-flatpickr'
import 'flatpickr/dist/themes/light.css'
import { dateFormat } from '@utils/utilities'
import { FilterDropdownProps } from '@utils/types'
import { NOTIFICATION_TYPE, PROFILE_STAGE, PROGRAM_TYPE } from '@utils/enum'
import { GRAPH_ERROR_MESSAGE, LEADS } from '@utils/translation'
import { optionsForFilterDropdown } from '@utils/StaticDataSet'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { Notification } from '@utils/utilities'
import cn from 'clsx'
import { useAmbassadorContext } from '@context/context'
import { applicationStatusTitle } from '@utils/constants'
import { useLocation } from 'react-router-dom'

export const FilterDropdown: React.FC<FilterDropdownProps> = ({
  items,
  startDate,
  endDate,
  setStartDateFilter,
  setEndDateFilter,
  applyYearlyMetrics,
  cameFrom,
  resetKey,
}) => {
  const location = useLocation()
  const [isOpenDate, setIsOpenDate] = useState(false)
  const [isCustomClick, setIsCustomClick] = useState(false)
  const flatpickrInstance = useRef<Flatpickr | null>(null)
  const divRef = useRef<HTMLDivElement | null>(null)
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const [filterValue, setFilterValue] = useState(items[0])
  const [showCustomDate, setShowCustomDate] = useState(false)
  let today = new Date()

  const { myProfileValue } = useAmbassadorContext()

  const click30Days = () => {
    const last30Days = new Date()
    last30Days.setDate(last30Days.getDate() - 30)
    const lastDate = new Date()
    lastDate.setDate(lastDate.getDate() - 1)

    setStartDateFilter(last30Days.toISOString())
    setEndDateFilter(lastDate.toISOString())
    applyYearlyMetrics(false)
  }

  const clickLastMonth = () => {
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
    // Set the date to the last day of the previous month
    const lastDayOfPreviousMonth = new Date(firstDayOfMonth)
    lastDayOfPreviousMonth.setDate(lastDayOfPreviousMonth.getDate() - 1)
    // Set the date to the first day of the previous month
    const firstDayOfPreviousMonth = new Date(
      lastDayOfPreviousMonth.getFullYear(),
      lastDayOfPreviousMonth.getMonth(),
      1
    )
    // set utc time for last month
    const utcFirstDate = new Date(
      firstDayOfPreviousMonth.getTime() -
        firstDayOfPreviousMonth.getTimezoneOffset() * 60000
    )
    const utcLastDate = new Date(
      lastDayOfPreviousMonth.getTime() -
        lastDayOfPreviousMonth.getTimezoneOffset() * 60000
    )

    setStartDateFilter(utcFirstDate.toISOString())
    setEndDateFilter(utcLastDate.toISOString())
    applyYearlyMetrics(false)
  }

  const clickLastWeek = () => {
    const currentDayOfWeek = today.getDay()
    // Calculate the number of days to subtract to get to the start of the last week
    const daysToSubtract = currentDayOfWeek + 7 // Add 7 to ensure we're going back a full week
    const lastWeekStartDate = new Date(today)
    lastWeekStartDate.setDate(today.getDate() - daysToSubtract)

    // Calculate the end date of the last week (6 days after the start date)
    const lastWeekEndDate = new Date(lastWeekStartDate)
    lastWeekEndDate.setDate(lastWeekStartDate.getDate() + 6)

    setStartDateFilter(lastWeekStartDate.toISOString())
    setEndDateFilter(lastWeekEndDate.toISOString())
    applyYearlyMetrics(false)
  }

  const clickLast7Days = () => {
    const last7Days = new Date()
    last7Days.setDate(last7Days.getDate() - 7)
    const lastDate = new Date()
    lastDate.setDate(lastDate.getDate() - 1)

    setStartDateFilter(last7Days.toISOString())
    setEndDateFilter(lastDate.toISOString())
    applyYearlyMetrics(false)
  }

  const clickToday = () => {
    setStartDateFilter(today.toISOString())
    setEndDateFilter(today.toISOString())
    applyYearlyMetrics(false)
  }

  const clickYesterday = () => {
    const yesterday = new Date()
    yesterday.setDate(yesterday.getDate() - 1)

    setStartDateFilter(yesterday.toISOString())
    setEndDateFilter(yesterday.toISOString())
    applyYearlyMetrics(false)
  }

  const clickCurrentYear = () => {
    const startYearDate = moment().utc().startOf('year')

    setStartDateFilter(startYearDate.toISOString())
    setEndDateFilter(today.toISOString())
    applyYearlyMetrics(true)
  }

  const clickLastYear = () => {
    const firstDateOfPreviousYear = moment()
      .utc()
      .subtract(1, 'year')
      .startOf('year')

    const lastDateOfPreviousYear = moment()
      .utc()
      .subtract(1, 'year')
      .endOf('year')

    setStartDateFilter(firstDateOfPreviousYear.toISOString())
    setEndDateFilter(lastDateOfPreviousYear.toISOString())
    applyYearlyMetrics(true)
  }

  const clickHandler = (val: any) => {
    setShowCustomDate(false)

    switch (val) {
      case 'Last 30 Days':
        click30Days()
        break
      case 'Last Months':
        clickLastMonth()
        break
      case 'Last Week':
        clickLastWeek()
        break

      case 'Last 7 days':
        clickLast7Days()
        break

      case 'Today':
        clickToday()
        break

      case 'Yesterday':
        clickYesterday()
        break

      case 'Current Year':
        clickCurrentYear()
        break
      case 'Last Year':
        clickLastYear()
        break
      default:
        break
    }
    setFilterValue(val)
  }
  const selectedDates = (dates: any) => {
    const parsedDate = moment(
      dates[0],
      'ddd MMMM DD YYYY HH:mm:ss [GMT]ZZ (z)',
      'Asia/Kolkata'
    )
    const parsedDateTwo = moment(
      dates[1],
      'ddd MMMM DD YYYY HH:mm:ss [GMT]ZZ (z)',
      'Asia/Kolkata'
    )

    const utcDateFirst = parsedDate.format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
    const utcDatetwo = parsedDateTwo.format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')

    // Calculate totel no of selected days fron start to end date
    const oneDay = 24 * 60 * 60 * 1000
    const totelNoOFSelectedDays = Math.round(
      Math.abs((dates[1] - dates[0]) / oneDay) + 1
    )

    if (totelNoOFSelectedDays <= 365) {
      if (dates.length === 2) {
        setStartDateFilter(utcDateFirst)
        setEndDateFilter(utcDatetwo)
        setIsCustomClick(false)
        setShowCustomDate(true)
      }
    } else if (dates[0] && dates[1]) {
      Notification(GRAPH_ERROR_MESSAGE, NOTIFICATION_TYPE.ERROR)
    }
  }

  const isPerformanceRoute = location.pathname.includes('performance')
  const isApproved =
    myProfileValue?.applicationStatus === applicationStatusTitle.APPROVED
  const hasCoupon = !!myProfileValue?.couponCode
  const isCampusMeded =
    myProfileValue?.programType === PROGRAM_TYPE.CAMPUS_MEDED

  const getCalendarPosition = () => {
    if (cameFrom === PROGRAM_TYPE.CSC_PROGRAM) {
      return 'top-[567px] xs:top-[510px] md:top-[500px] lg:top-[590px] right-2 xxs:right-[40px] sm:right-[25px] xl:right-[183px]'
    }
    if (isPerformanceRoute) {
      return 'top-[334px] xxs:top-[280px] sm:top-[314px] md:top-[333px] lg:top-[330px] right-8 lg:right-[100px]'
    }

    if (!isApproved && !isCampusMeded) {
      return 'top-[667px] xs:top-[567px] md:top-[500px] lg:top-[520px] right-2 xxs:right-[40px] lg:right-[99px]'
    }

    if (isApproved && !hasCoupon && !isCampusMeded) {
      return 'top-[1180px] xxs:top-[1084px] md:top-[1029px] lg:top-[1000px] xl:top-[753px] right-8 lg:right-[100px]'
    }

    if (isApproved && !isCampusMeded) {
      return 'top-[1180px] xxs:top-[1084px] xs:top-[947px] sm:top-[950px] lg:top-[853px] right-2 xxs:right-8 lg:right-[100px]'
    }

    if (!isApproved && isCampusMeded) {
      return 'top-[600px] xs:top-[567px] md:top-[500px] lg:top-[530px] right-2 xxs:right-[40px] lg:right-[99px]'
    }

    if (isApproved && !hasCoupon && isCampusMeded) {
      return 'top-[450px] xxs:top-[447px] sm:top-[390px] md:top-[390px] lg:top-[380px] right-8 lg:right-[100px]'
    }

    if (isApproved && hasCoupon && isCampusMeded) {
      return 'top-[490px] xxs:top-[490px] sm:top-[400px] md:top-[440px] lg:top-[469px] right-8 lg:right-[100px]'
    }

    return ''
  }

  const calendarSelect = cn(
    'z-30 flex flex-col !bg-white justify-center items-center absolute cursor-pointer',
    getCalendarPosition()
  )

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        divRef.current &&
        dropdownRef.current &&
        !divRef.current?.contains(event.target as Node) &&
        !dropdownRef.current?.contains(event.target as Node)
      ) {
        setIsCustomClick(false)
      }
    }

    document.addEventListener('click', handleClickOutside)

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    setFilterValue(items[0])
    setShowCustomDate(false)
    setIsCustomClick(false)
  }, [resetKey])

  return (
    <div className="text-right bg-white flex items-center justify-start md:justify-end">
      <Menu as="div" className="relative inline-block text-left">
        <div>
          <Menu.Button className="h-[48px] inline-flex w-full items-center justify-center rounded-md bg-white px-4 py-2 text-base font-medium text-[#757575] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75 border border-[#B7B7B7]">
            {startDate && endDate && showCustomDate
              ? `${dateFormat(startDate)}-${dateFormat(endDate)}`
              : filterValue}
            <ChevronDownIcon
              className="mr-1 ml-2 h-5 w-5 text-violet-200 hover:text-violet-100"
              aria-hidden="true"
            />
          </Menu.Button>
        </div>
        {
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 mt-2 w-64 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none z-10">
              {items.map((item: string, idx: number) => {
                return idx !== items.length - 1 ? (
                  <Menu.Item>
                    <div
                      className={`p-2 m-2 cursor-pointer hover:bg-[#B7B7B7] ${
                        filterValue === item && 'bg-[#F1EFFF]'
                      }`}
                      onClick={() => clickHandler(item)}
                    >
                      {item}
                    </div>
                  </Menu.Item>
                ) : (
                  cameFrom != LEADS && (
                    <Menu.Item>
                      <div
                        className="p-2 m-2 cursor-pointer"
                        onClick={() => {
                          setIsOpenDate(true)
                          setIsCustomClick(true)
                          setFilterValue(item)
                        }}
                        ref={dropdownRef}
                      >
                        {item}
                      </div>
                    </Menu.Item>
                  )
                )
              })}
            </Menu.Items>
          </Transition>
        }
      </Menu>
      {isOpenDate && (
        <div className={calendarSelect} ref={divRef}>
          {isCustomClick && (
            <Flatpickr
              className="hidden"
              options={optionsForFilterDropdown}
              onChange={(dates: any) => selectedDates(dates)}
              ref={(flatpickr: any) => (flatpickrInstance.current = flatpickr)}
            />
          )}
        </div>
      )}
    </div>
  )
}
